import { useState, useEffect, JSX } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import fetchWithAuth from '../utils/fetchWithAuth';
import BrandSpinner from '../components/ui/atoms/BrandSpinner/BrandSpinner';
import BackButton from '../components/ui/atoms/BackButton';
import {
  MdShoppingCartCheckout,
  MdDone,
  MdCalendarMonth,
} from 'react-icons/md';
import { GiWineBottle } from 'react-icons/gi';
import { LuVegan } from 'react-icons/lu';
import { PiCowFill } from 'react-icons/pi';
import {
  GiTomato,
  GiChickenLeg,
  GiWaterBottle,
  GiSlicedBread,
} from 'react-icons/gi';
import { FaIceCream, FaCandyCane, FaFlask, FaPepperHot } from 'react-icons/fa';
import { scaledAmountParts } from '../utils/unitDisplay.ts';

const categoryIcons: Record<string, JSX.Element | null> = {
  'Fresh Produce': <GiTomato className="text-gray-700" />,
  'Bakery & Grains': <GiSlicedBread className="text-gray-700" />,
  'Pantry & Dry Goods': <FaPepperHot className="text-gray-700" />,
  Meat: <GiChickenLeg className="text-gray-700" />,
  Dairy: <PiCowFill className="text-gray-700" />,
  Vegan: <LuVegan className="text-gray-700" />,
  Alcohol: <GiWineBottle className="text-gray-700" />,
  Beverages: <GiWaterBottle className="text-gray-700" />,
  'Frozen Foods': <FaIceCream className="text-gray-700" />,
  'Sweets & Snacks': <FaCandyCane className="text-gray-700" />,
  Additives: <FaFlask className="text-gray-700" />,
  'Misc.': null,
};

type Item = {
  id: string;
  ingredientId: string;
  name: string;
  unit: string;
  requiredAmount: number;
  haveAmount: number;
  isRemoved: boolean;
  isChecked: boolean;
  category: string;
};

type ShoppingList = {
  id: string;
  dateStart: string;
  dateEnd: string;
  items: Item[];
};

// Reusable component for each row (both “to buy” and “bought”)
type ShoppingListItemProps = {
  item: Item;
  isBought: boolean;
  index?: number; // only used for alternating bg on “to buy”
  onToggle: (itemId: string, checked: boolean) => void;
};

function ShoppingListItem({
  item,
  isBought,
  index = 0,
  onToggle,
}: ShoppingListItemProps) {
  const baseClasses =
    'p-3 rounded-lg border flex items-center justify-between active:scale-[0.98] transition-transform';
  const toBuyBg = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

  // label rules: never show numbers for qualitative units
  const unitLower = (item.unit || '').toLowerCase();
  const isQual = unitLower === 'to taste' || unitLower === 'as needed';

  let label: string;
  if (isQual) {
    label = item.unit || '';
  } else {
    const req = typeof item.requiredAmount === 'number' ? item.requiredAmount : 0;
    const diff = Math.max(0, req - (item.haveAmount || 0));
    if (diff <= 0) {
      // no amount to show, just the unit
      label = item.unit || '';
    } else {
      const { amountStr, unitStr } = scaledAmountParts(diff, 1, 1, item.unit || '');
      label = `${amountStr} ${unitStr}`.trim();
    }
  }

  if (!isBought) {
    return (
      <li
        onClick={() => onToggle(item.id, true)}
        className={`${baseClasses} ${toBuyBg} hover:shadow-sm`}
      >
        <span className="text-sm font-medium text-gray-900">{item.name}</span>
        <span className="text-xs font-medium text-gray-500">{label}</span>
      </li>
    );
  } else {
    return (
      <li
        onClick={() => onToggle(item.id, false)}
        className={`${baseClasses} bg-gray-100 text-gray-400`}
      >
        <span className="text-sm">{item.name}</span>
        <span className="text-sm">{label}</span>
      </li>
    );
  }
}

function formatDateRange(startStr: string, endStr: string): string {
  const start = new Date(startStr);
  const end = new Date(endStr);

  const formatDay = (day: number) => {
    const suffix =
      [, 'st', 'nd', 'rd'][day % 10] && ![11, 12, 13].includes(day % 100)
        ? [, 'st', 'nd', 'rd'][day % 10]
        : 'th';
    return `${day}${suffix}`;
  };

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const sameDay = sameMonth && start.getDate() === end.getDate();

  const startDay = formatDay(start.getDate());
  const endDay = formatDay(end.getDate());

  const startMonth = start.toLocaleString('default', { month: 'long' });
  const endMonth = end.toLocaleString('default', { month: 'long' });
  const year = start.getFullYear();

  if (sameDay) {
    return `${startMonth} ${startDay}, ${year}`;
  }
  if (sameMonth) {
    return `${startMonth} ${startDay} – ${endDay}, ${year}`;
  }
  if (sameYear) {
    return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
  }
  return `${startMonth} ${startDay}, ${start.getFullYear()} – ${endMonth} ${endDay}, ${end.getFullYear()}`;
}

export default function ShoppingListView() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(search);
  const dateStart = params.get('dateStart');
  const dateEnd = params.get('dateEnd');

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<ShoppingList | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [prevBought, setPrevBought] = useState(0);

  useEffect(() => {
    if (!dateStart || !dateEnd) {
      navigate('/shopping-list-prep');
      return;
    }
    async function load() {
      setLoading(true);
      try {
        const data: ShoppingList = await fetchWithAuth(
          `/api/shopping-list?dateStart=${dateStart}&dateEnd=${dateEnd}`
        );
        setList(data);
        setItems(data.items.filter((it) => !it.isRemoved));
      } catch (err) {
        console.error('Failed to load shopping list:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [dateStart, dateEnd, navigate]);

  const toggleChecked = async (itemId: string, checked: boolean) => {
    try {
      await fetchWithAuth(`/api/shopping-list/item/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isChecked: checked }),
      });
      setItems((prev) =>
        prev.map((it) =>
          it.id === itemId ? { ...it, isChecked: checked } : it
        )
      );
    } catch (err) {
      console.error('Failed to update item:', err);
    }
  };

  if (loading) {
    return (
      <div className="mt-8 text-center">
        <BrandSpinner />
      </div>
    );
  }

  if (!list) {
    return (
      <div className="p-4 text-center text-red-500">
        No shopping list found.{' '}
        <button
          onClick={() => navigate('/shopping-list-prep')}
          className="text-blue-600 underline"
        >
          Go back
        </button>
      </div>
    );
  }

  const toBuy = items.filter((it) => !it.isChecked);
  const bought = items.filter((it) => it.isChecked);

  const groupByCategory = (items: Item[]) => {
    const grouped: Record<string, Item[]> = {};
    for (const item of items) {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    }
    return grouped;
  };

  const categoryOrder = [
    'Fresh Produce',
    'Pantry & Dry Goods',
    'Bakery & Grains',
    'Sweets & Snacks',
    'Dairy',
    'Meat',
    'Vegan',
    'Beverages',
    'Alcohol',
    'Frozen Foods',
    'Additives',
    'Misc',
  ];

  const groupedToBuy = groupByCategory(toBuy);

  const safeIndex = (cat: string) => {
    const i = categoryOrder.indexOf(cat);
    return i === -1 ? categoryOrder.length : i;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ── Fixed Header: Back Button + Date Info ── */}
      {/* <div className="fixed top-0 inset-x-0 z-50 bg-white h-[72px] px-4 flex items-center justify-between"> */}
      <div
        className="fixed inset-x-0 z-50 bg-white h-[72px] px-4 flex items-center justify-between"
        style={{ top: 'var(--safe-top)' }}
      >
        <div className="flex items-center gap-3">
          <BackButton />
        </div>
        <div className="px-3 py-2 items-center gap-2">
          <div className="flex items-center gap-2 text-primary text-sm font-semibold">
            <span>{formatDateRange(list.dateStart, list.dateEnd)}</span>
            <MdCalendarMonth className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* ── Sticky Tabs ── */}
      {/* <div className="sticky top-[72px] z-40 bg-white border-b grid grid-cols-2 h-[40px]"> */}
      <div
        className="sticky inset-x-0 z-40 bg-white border-b grid grid-cols-2 h-[40px]"
        style={{ top: 'calc(var(--safe-top) + 72px)' }}
      >
        <button
          onClick={() => setPrevBought(0)}
          className={`w-full text-center h-full text-sm flex items-center justify-center gap-2 ${
            prevBought === 0
              ? 'border-b-2 border-primary text-primary'
              : 'text-gray-500'
          }`}
        >
          <MdShoppingCartCheckout className="w-4 h-4" />
          <span>To Buy</span>
          <span className="text-xs font-medium whitespace-nowrap">
            ({items.length - bought.length})
          </span>
        </button>
        <button
          onClick={() => setPrevBought(1)}
          className={`w-full text-center h-full text-sm flex items-center justify-center gap-2 ${
            prevBought === 1
              ? 'border-b-2 border-primary text-primary'
              : 'text-gray-500'
          }`}
        >
          <MdDone className="w-4 h-4" />
          <span>Bought</span>
          <span className="text-xs font-medium whitespace-nowrap">
            ({bought.length})
          </span>
        </button>
      </div>

      {/* ── Scrollable Content ── */}
      <div
        className="pb-[140px] px-4 space-y-6"
        style={{ paddingTop: '72px' }}
      >
        {prevBought === 0 ? (
          <div>
            {toBuy.length === 0 ? (
              <p className="text-sm text-gray-500">All items bought 🎉</p>
            ) : (
              <ul className="space-y-2">
                {Object.entries(groupedToBuy)
                  .sort((a, b) => safeIndex(a[0]) - safeIndex(b[0]))
                  .map(([category, groupItems], catIdx) => (
                    <div
                      key={category}
                      className={`${catIdx !== 0 ? 'mt-8' : ''} mb-6`}
                    >
                      <h3 className="flex items-center gap-2 text-xl font-semibold text-gray-800 mb-3">
                        <span className="w-6 h-6">
                          {categoryIcons[category] && (
                            <span className="w-6 h-6">
                              {categoryIcons[category]}
                            </span>
                          )}
                        </span>
                        <span className="capitalize">{category}</span>
                      </h3>
                      <ul className="space-y-2">
                        {groupItems.map((it, idx) => (
                          <ShoppingListItem
                            key={it.id}
                            item={it}
                            isBought={false}
                            index={idx}
                            onToggle={toggleChecked}
                          />
                        ))}
                      </ul>
                    </div>
                  ))}
              </ul>
            )}
          </div>
        ) : (
          <div>
            {bought.length === 0 ? (
              <p className="text-sm text-gray-500">No items bought yet</p>
            ) : (
              <ul className="space-y-2">
                {bought.map((it) => (
                  <ShoppingListItem
                    key={it.id}
                    item={it}
                    isBought={true}
                    onToggle={toggleChecked}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
